import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiUserCheck, FiUserX } from 'react-icons/fi';
import { api } from '../api';
import { useMe } from '../App';
import { findPerm } from '../perm';

export default function Users() {
  const me = useMe();
  const perm = findPerm(me.modules, 'users');
  const [rows, setRows] = useState(null);
  const [opts, setOpts] = useState({ roles: [], departments: [], managers: [] });
  const [modal, setModal] = useState(null);
  const [toast, setToast] = useState('');

  async function load() {
    try {
      const [users, options] = await Promise.all([api('/users'), api('/users/form-options')]);
      setRows(users); setOpts(options);
    } catch (e) { setToast(e.message); }
  }
  useEffect(() => { load(); }, []);
  function flash(m) { setToast(m); setTimeout(() => setToast(''), 2800); }

  async function toggleActive(u) {
    try { await api(`/users/${u.id}/active`, { method: 'PATCH', body: { is_active: !u.is_active } });
      flash(u.is_active ? 'User deactivated' : 'User activated'); load(); }
    catch (e) { flash(e.message); }
  }
  async function remove(u) {
    if (!window.confirm(`Delete user "${u.name}"? This cannot be undone.`)) return;
    try { await api('/users/' + u.id, { method: 'DELETE' }); flash('User deleted'); load(); }
    catch (e) { flash(e.message); }
  }

  return (
    <div>
      <div className="mod-head">
        <div className="titles">
          <div className="page-eyebrow">Masters</div>
          <h1>Users</h1>
          <div className="count">{rows ? `${rows.length} user${rows.length !== 1 ? 's' : ''}` : 'Loading…'}</div>
        </div>
        {perm.can_create && (
          <button className="btn btn-dark" onClick={() => setModal({ mode: 'add', user: {} })}>
            <FiPlus /> Add user
          </button>
        )}
      </div>

      <div className="table-card">
        {!rows ? <div className="empty">Loading…</div> :
         rows.length === 0 ? <div className="empty"><h3>No users yet</h3><p>Add your first user to get started.</p></div> :
          <table className="data">
            <thead>
              <tr>
                <th>Name</th><th>Login</th><th>Role</th><th>Reports to</th><th>Department</th><th>Status</th>
                {(perm.can_edit || perm.can_delete) && <th style={{ textAlign: 'right' }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.id} style={{ opacity: u.is_active ? 1 : 0.55 }}>
                  <td style={{ fontWeight: 600 }}>{u.name}</td>
                  <td style={{ color: 'var(--muted)' }}>{u.email || u.employee_id}</td>
                  <td>{u.role_name}</td>
                  <td style={{ color: 'var(--muted)' }}>{u.manager_name || '—'}</td>
                  <td style={{ color: 'var(--muted)' }}>{u.department_name || '—'}</td>
                  <td>
                    <span className="pill" style={u.is_active
                      ? { background: '#E9F3EC', color: '#3B7A4E' }
                      : { background: '#F3EFE7', color: '#8A7B5E' }}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  {(perm.can_edit || perm.can_delete) && (
                    <td>
                      <div className="row-actions">
                        {perm.can_edit && <button className="btn btn-sm" onClick={() => setModal({ mode: 'edit', user: u })}><FiEdit2 /> Edit</button>}
                        {perm.can_edit && (
                          <button className="btn btn-sm" onClick={() => toggleActive(u)} title={u.is_active ? 'Deactivate' : 'Activate'}>
                            {u.is_active ? <FiUserX /> : <FiUserCheck />}
                          </button>
                        )}
                        {perm.can_delete && <button className="btn btn-sm btn-danger" onClick={() => remove(u)}><FiTrash2 /></button>}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>}
      </div>

      {modal && <UserModal {...modal} opts={opts}
        onClose={() => setModal(null)} onSaved={(m) => { setModal(null); flash(m); load(); }} />}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

function UserModal({ mode, user, opts, onClose, onSaved }) {
  const [f, setF] = useState({
    name: user.name || '', email: user.email || '', employee_id: user.employee_id || '',
    role_id: user.role_id || '', reports_to: user.reports_to || '',
    department_id: user.department_id || '', password: '',
  });
  const [error, setError] = useState(''); const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  async function save() {
    setError(''); setBusy(true);
    const body = {
      name: f.name, email: f.email, employee_id: f.employee_id,
      role_id: f.role_id || null, reports_to: f.reports_to || null,
      department_id: f.department_id || null,
    };
    if (f.password) body.password = f.password;
    try {
      if (mode === 'add') { await api('/users', { method: 'POST', body }); onSaved('User created'); }
      else { await api('/users/' + user.id, { method: 'PUT', body }); onSaved('User updated'); }
    } catch (e) { setError(e.message); setBusy(false); }
  }

  // managers list excludes the user being edited (can't report to self)
  const managerOpts = opts.managers.filter((m) => m.id !== user.id);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <h2>{mode === 'add' ? 'Add user' : 'Edit user'}</h2>

        <div className="field"><label>Full name</label>
          <input value={f.name} autoFocus onChange={set('name')} placeholder="e.g. Anita Menon" /></div>

        <div className="grid2">
          <div className="field"><label>Email</label>
            <input value={f.email} onChange={set('email')} placeholder="name@company.com" /></div>
          <div className="field"><label>Employee ID</label>
            <input value={f.employee_id} onChange={set('employee_id')} placeholder="Optional" /></div>
        </div>

        <div className="grid2">
          <div className="field"><label>Role</label>
            <select value={f.role_id} onChange={set('role_id')}>
              <option value="">Select role…</option>
              {opts.roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select></div>
          <div className="field"><label>Department</label>
            <select value={f.department_id} onChange={set('department_id')}>
              <option value="">None</option>
              {opts.departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select></div>
        </div>

        <div className="field"><label>Reports to</label>
          <select value={f.reports_to} onChange={set('reports_to')}>
            <option value="">None (top of chain)</option>
            {managerOpts.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select></div>

        <div className="field">
          <label>{mode === 'add' ? 'Initial password' : 'New password (leave blank to keep current)'}</label>
          <input type="password" value={f.password} onChange={set('password')} placeholder="At least 8 characters" />
        </div>

        {error && <div className="inline-error">{error}</div>}
        <div className="modal-actions">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-dark" onClick={save} disabled={busy || !f.name.trim() || !f.role_id}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
